import DotsHorizontalIcon from 'mdi-react/DotsHorizontalIcon';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Badge,
  Button,
  Col,
  Container,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Row,
  Table,
  UncontrolledDropdown,
} from 'reactstrap';
import ArrowUpIcon from 'mdi-react/ArrowUpIcon';
import ArrowDownIcon from 'mdi-react/ArrowDownIcon';
import CheckIcon from '../../../shared/components/custom/CheckIcon';
import useModal from '../../../shared/hooks/useModal';
import Api from '../../../util/api';
import DeleteModal from '../../../shared/components/custom/DeleteModal';
import Pagination from '../../../shared/components/pagination/Pagination';
import t, { partial } from '../../../util/translation/translation';
import { formatDate } from '../../../util/date-and-time';
import { formatCurrencyNumber } from '../../../util/functions';
import useConfig from '../../../util/useConfig';
import Select from 'react-select';
import Loading from '../../../shared/components/custom/Loading';

const PAYOUT_STATUS_KEYS = {
  SCHEDULED: 'payoutStatusScheduled',
  PROCESSING: 'payoutStatusProcessing',
  SENT: 'payoutStatusSent',
  PAID: 'payoutStatusPaid',
  FAILED: 'payoutStatusFailed',
};

const LotteryTicketsPage = ({
  lotteryId,
  drawType,
  drawDate,
  refreshLottery,
}) => {
  const config = useConfig();
  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState([]);
  const [handleModal, visible, deleteID] = useModal();
  const [handlePrizeModal, visiblePrizeModal, claimTicketID] = useModal();
  const [payeeSSN, setPayeeSSN] = useState('');
  const [pagination, setPagination] = useState({
    total: 0,
    currentPage: 1,
    perPage: 50,
  });
  const [order, setOrder] = useState({
    orderBy: 'createdAt',
    desc: true,
  });
  const canDrawLottery =
    drawType === 'manual' && tickets.length > 0 && drawDate === null;
  const drawButtonVisible = drawType === 'manual';
  const p = partial('lotteries');
  const s = partial('shared');
  const statusOptions = [
    { label: t('transactions.status-paid'), value: 'PAID' },
    { label: t('transactions.status-pending'), value: 'PENDING' },
    { label: t('transactions.status-failed'), value: 'FAILED' },
  ];
  const [selectedStatus, setSelectedStatus] = useState({
    label: 'Paid',
    value: 'PAID',
  });
  const fetchTickets = async (
    currentPage = 1,
    pageSize = 50,
    orderObject = {
      orderBy: 'createdAt',
      desc: true,
    },
    status = 'PAID'
  ) => {
    setLoading(true);
    try {
      const resTickets = await Api.lottery.tickets.getTickets(
        lotteryId,
        { page: currentPage, take: pageSize },
        {
          orderBy: orderObject.orderBy,
          direction: orderObject.desc ? 'DESC' : 'ASC',
        },
        status
      );
      const uniqueUserIds = [
        ...new Set(
          resTickets.data.map((ticket) => ticket.userId).filter(Boolean)
        ),
      ];
      const { data: usersByIds } =
        await Api.users.getUsersByCloudIds(uniqueUserIds);
      const userByIdMap = {};
      for (let i = 0; i < usersByIds.length; i++) {
        userByIdMap[usersByIds[i].userCloudId] = usersByIds[i];
      }
      const ticketsWithUsers = resTickets.data.map((ticket) => {
        let constructedUser = userByIdMap[ticket.userId];
        if (!constructedUser) {
          constructedUser = {
            email: ticket.email,
            phoneNumber: ticket.phoneNumber,
          };
        }
        return {
          ...ticket,
          user: constructedUser,
        };
      });
      setTickets(ticketsWithUsers);
      setPagination((old) => ({ ...old, total: resTickets.total }));
      setLoading(false);
    } catch (err) {
      toast.error(p('lotteriesRetrievingERROR'));
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchTickets();
  }, []);
  const deleteTicket = async () => {
    setLoading(true);
    try {
      await Api.lottery.tickets.deleteTicket(deleteID);
      toast.success(p('ticketDeleted'));
    } catch (err) {
      toast.error(p('ticketDeletedERROR'));
    } finally {
      handleModal();
      fetchTickets(pagination.currentPage, pagination.perPage, order);
    }
  };

  const onChangePage = (pager) => {
    if (
      pager.pageSize !== pagination.perPage ||
      pager.currentPage !== pagination.currentPage
    ) {
      setPagination((old) => ({
        ...old,
        currentPage: pager.currentPage,
        perPage: pager.pageSize,
      }));
      fetchTickets(pager.currentPage, pager.pageSize, order);
    }
  };

  const renderSort = (cellOrder) => {
    if (order.orderBy !== cellOrder) {
      return undefined;
    }
    return order.desc ? <ArrowDownIcon size={16} /> : <ArrowUpIcon size={16} />;
  };
  const handleSort = (incommingOrder) => {
    const newOrder = {
      orderBy: incommingOrder,
      desc: incommingOrder === order.orderBy ? !order.desc : true,
    };
    setOrder(newOrder);
    setPagination({
      total: 0,
      currentPage: 1,
      perPage: 50,
    });
    fetchTickets(1, 50, newOrder);
  };

  const renderTickets = () => {
    const getNameLink = (ticket) => {
      if (ticket.userId) {
        return (
          <Link
            to={`/users/edit/${ticket.userId}`}
          >{`${ticket.user?.firstname} ${ticket.user?.lastname} (${ticket.user?.phoneNumber})`}</Link>
        );
      }
      return <span>{`${ticket.user?.phoneNumber || '-'}`}</span>;
    };
    const getStatusBadge = (ticket) => {
      switch (ticket.paymentStatus) {
        case 'PAID':
          return (
            <Badge color={'success'}>{t('transactions.status-paid')}</Badge>
          );
        case 'PENDING':
          return (
            <Badge color={'warning'}>{t('transactions.status-pending')}</Badge>
          );
        case 'FAILED':
          return (
            <Badge color={'danger'}>{t('transactions.status-failed')}</Badge>
          );
        default:
          return (
            <Badge color={'default'}>{t('transactions.status-pending')}</Badge>
          );
      }
    };
    const getWinningAmount = (ticket) => {
      if (ticket.hasWon) {
        if (ticket.winningAmount === null) {
          return (
            <Link
              to={`/lottery/prizes/edit/${ticket.prize?.id}`}
            >{`${ticket.prize?.title}`}</Link>
          );
        }
        return `${formatCurrencyNumber(ticket.winningAmount)} ${config.currencyCaps}`;
      }
      return '-';
    };
    const getPayoutCell = (ticket) => {
      const items = ticket.payoutQueueItems || [];
      if (!items.length) {
        return ticket.payoutFinished ? (
          <CheckIcon state={true} />
        ) : (
          <span>-</span>
        );
      }
      const failed = items.some((item) => item.status === 'FAILED');
      const allPaid = items.every((item) => item.status === 'PAID');
      const allSentOrPaid = items.every(
        (item) => item.status === 'PAID' || item.status === 'SENT'
      );
      const paid = items
        .filter((item) => item.status === 'PAID' || item.status === 'SENT')
        .reduce((sum, item) => sum + item.amount, 0);

      let badge;
      if (failed) {
        badge = <Badge color="danger">{p('payoutStatusFailed')}</Badge>;
      } else if (allPaid) {
        badge = <Badge color="success">{p('payoutStatusPaid')}</Badge>;
      } else if (allSentOrPaid) {
        badge = (
          <Badge color="success">
            {paid}/{ticket.winningAmount}
          </Badge>
        );
      } else {
        badge = (
          <Badge color="warning">
            {paid}/{ticket.winningAmount}
          </Badge>
        );
      }

      return (
        <div>
          {badge}
          {renderPayoutItems(items)}
        </div>
      );
    };
    const renderPayoutItems = (items) => (
      <div style={{ fontSize: 11, color: '#6C6C6C', marginTop: 4 }}>
        {items.map((item) => (
          <div key={item.id}>
            {formatCurrencyNumber(item.amount)} ·{' '}
            {p(PAYOUT_STATUS_KEYS[item.status] || 'payoutStatusScheduled')} ·{' '}
            {item.sentAt
              ? `${p('payoutStatusSent')}: ${formatDate(item.sentAt)}`
              : `${p('payoutStatusScheduled')}: ${
                  item.scheduledFor ? formatDate(item.scheduledFor) : '-'
                }`}
          </div>
        ))}
      </div>
    );

    return tickets.map((ticket, i) => (
      <tr
        style={{ background: i % 2 ? '#fff' : '#F9FAFB' }}
        key={`ticket-${ticket.id}`}
      >
        <td>
          <div className="flex-Vcenter">{getNameLink(ticket)}</div>
        </td>
        <td>{formatDate(ticket.createdAt)}</td>
        <td>
          {formatCurrencyNumber(ticket.price)} {config.currencyCaps}
        </td>
        <td>{getStatusBadge(ticket)}</td>
        <td>
          <CheckIcon state={ticket.hasWon} />
        </td>
        <td>{getWinningAmount(ticket)}</td>
        <td>{getPayoutCell(ticket)}</td>

        <td>
          <UncontrolledDropdown className="dashboard__table-more">
            <DropdownToggle>
              <DotsHorizontalIcon />
            </DropdownToggle>
            <DropdownMenu className="dropdown__menu">
              {ticket.hasWon &&
                !ticket.payoutFinished &&
                ticket.winningAmount !== null && (
                  <DropdownItem onClick={() => handlePrizeModal(ticket.id)}>
                    {p('payout')}
                  </DropdownItem>
                )}
              {ticket.payoutQueueItems?.some(
                (item) => item.status === 'FAILED'
              ) && (
                <DropdownItem onClick={() => handleRetryPayout(ticket)}>
                  {p('payoutRetry')}
                </DropdownItem>
              )}
              <DropdownItem
                onClick={() => handleModal(ticket.id)}
                className="danger"
              >
                {s('delete')}
              </DropdownItem>
            </DropdownMenu>
          </UncontrolledDropdown>
        </td>
      </tr>
    ));
  };
  const handleDrawLottery = async () => {
    try {
      setLoading(true);
      await Api.lottery.tickets.drawLottery(lotteryId);
      toast.success(p('lotteryDrawn'));
      fetchTickets();
      refreshLottery();
    } catch (err) {
      toast.error(p('lotteryDrawnERROR'));
      setLoading(false);
    }
  };
  const handleTicketPayout = async () => {
    try {
      setLoading(true);
      const payout = await Api.lottery.tickets.payoutTicket(
        claimTicketID,
        payeeSSN
      );
      const hasFailed = (payout?.queueItems || []).some(
        (item) => item.status === 'FAILED'
      );
      if (hasFailed) {
        toast.error(p('payoutRetryERROR'));
      } else {
        toast.success(p('lotteryTicketPaid'));
      }
      fetchTickets();
      refreshLottery();
      handlePrizeModal();
      setPayeeSSN('');
    } catch (err) {
      toast.error(p('lotteryTicketPaidERROR'));
      setLoading(false);
      handlePrizeModal();
      setPayeeSSN('');
    }
  };

  const handleRetryPayout = async (ticket) => {
    const failedItem = (ticket.payoutQueueItems || []).find(
      (item) => item.status === 'FAILED'
    );
    if (!failedItem) {
      return;
    }
    try {
      setLoading(true);
      await Api.lottery.tickets.retryPayoutQueueItem(failedItem.id);
      toast.success(p('payoutRetrySuccess'));
      fetchTickets();
    } catch (err) {
      toast.error(p('payoutRetryERROR'));
      setLoading(false);
    }
  };

  const handleStatusChange = (selectedOption) => {
    setSelectedStatus(selectedOption);
    fetchTickets(
      pagination.currentPage,
      pagination.perPage,
      order,
      selectedOption.value
    );
  };

  return (
    <Container>
      <Loading loading={loading} />
      <DeleteModal
        visible={visible}
        type={p('ticket')}
        handleModal={handleModal}
        modalAction={deleteTicket}
      />
      <DeleteModal
        visible={visiblePrizeModal}
        modalType="lotteryTicketPayout"
        canComment={true}
        handleModal={handlePrizeModal}
        modalAction={handleTicketPayout}
        onCommentChange={setPayeeSSN}
        commentPlaceholder={p('personSSN')}
      />
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <div style={{ width: 200, marginRight: 10 }}>
          <Select
            placeholder={s('status')}
            options={statusOptions}
            value={selectedStatus}
            onChange={handleStatusChange}
          />
        </div>
        {drawButtonVisible && (
          <div className="marginTop-0">
            <Button
              size="sm"
              color="primary"
              disabled={!canDrawLottery}
              onClick={handleDrawLottery}
            >
              {p('drawLottery')}
            </Button>
          </div>
        )}
      </div>

      <Row>
        <Col>
          <div
            style={{
              display: 'flex',
              paddingBottom: 5,
              background: '#fff',
              borderRadius: 6,
              boxShadow: '0px 2px 8px 0px rgba(0,0,0,0.08)',
            }}
          >
            <Table responsive striped>
              <thead>
                <tr>
                  <th style={{ color: '#4D576A' }}>
                    <div className="flex alignItems-center">{s('name')}</div>
                  </th>
                  <th
                    style={{ color: '#4D576A' }}
                    className="sortable"
                    onClick={() => handleSort('createdAt')}
                  >
                    <div className="flex alignItems-center">
                      {s('date')} {renderSort('createdAt')}
                    </div>
                  </th>
                  <th style={{ color: '#4D576A' }}>
                    <div className="flex alignItems-center">{s('price')}</div>
                  </th>
                  <th style={{ color: '#4D576A' }}>
                    <div className="flex alignItems-center">{s('status')}</div>
                  </th>
                  <th style={{ color: '#4D576A' }}>
                    <div className="flex alignItems-center">{p('hasWon')}</div>
                  </th>
                  <th style={{ color: '#4D576A' }}>
                    <div className="flex alignItems-center">
                      {p('winningAmount')}
                    </div>
                  </th>
                  <th style={{ color: '#4D576A' }}>
                    <div className="flex alignItems-center">
                      {p('payoutStatus')}
                    </div>
                  </th>
                  <th />
                </tr>
              </thead>
              <tbody>{renderTickets()}</tbody>
            </Table>
          </div>
          <Pagination
            items={pagination.total}
            onChangePage={onChangePage}
            initialPage={pagination.currentPage}
          />
        </Col>
      </Row>
    </Container>
  );
};

export default LotteryTicketsPage;
